/**
 * Admin step-up dialog — thin wrapper around `<StepUpSurface>`.
 *
 * Mounted exactly once at the app root. Subscribes to the
 * admin-local `useStepUpStore` (Zustand) and exposes:
 *
 *   - the api-client wiring for the admin step-up endpoint
 *     (`/api/admin/v1/sessions/step-up`); the begin endpoint is
 *     the shared ceremony substrate (`/api/ceremony/v1/...`).
 *   - the per-app store hooks adapted into the StepUpSurface
 *     contract (StepUpStoreBinding).
 *
 * Everything visible — picker, prompt, error banner, cancel,
 * dialog chrome — lives in `<StepUpSurface>` so the look + feel
 * is identical to the portal step-up and stays in lockstep with
 * the LoginSurface primitives every passkey ceremony consumes.
 */

import {
  StepUpSurface,
  type StepUpClient,
  type StepUpStoreBinding,
} from "@pureid/ui";
import type {
  AssertionOptionsWire,
  AssertionResultWire,
} from "@pureid/auth-core";
import { API_BASE, apiFetch } from "@/lib/api-client";
import { PLATFORM_TENANT_SLUG } from "@/lib/platform-tenant";
import { useStepUpStore } from "@/lib/with-step-up";
import { useRecommendedDefault } from "@/features/onboarding/queries";

// The step-up begin endpoint returns `session_id` (matching the
// rest of the loginflow + ceremony substrate); the step-up verify
// endpoint receives the same UUID under `ceremony_id`. We map at
// this boundary so the StepUpSurface's StepUpClient contract uses
// one neutral name (`ceremonyId`) end-to-end.
//
// IMPORTANT: begin uses /api/admin/v1/sessions/step-up/begin (NOT
// the public /api/ceremony/v1/authentication/begin). The dedicated
// endpoint reads the caller's user id from session middleware and
// builds a NON-DISCOVERABLE assertion with the per-credential
// transport filter applied — which is the only way to reliably
// force QR on desktop Chrome and the Edge browser when the user has local platform
// credentials. The public discoverable endpoint cannot apply the
// filter (no allowCredentials list to filter), and Chrome/Edge
// silently ignore the WebAuthn L3 hybrid hint in that case. See
// ceremony.AuthenticationBeginForUser doc-comment.
interface BeginResponse {
  session_id: string;
  options: AssertionOptionsWire;
}

const adminStepUpClient: StepUpClient = {
  begin: async (hint, opts) => {
    const body: Record<string, string> = {};
    if (hint !== "") body.hint = hint;
    const res = await apiFetch<BeginResponse>(
      "/api/admin/v1/sessions/step-up/begin",
      {
        method: "POST",
        body,
        tenantSlug: PLATFORM_TENANT_SLUG,
        ceremonyId: opts?.clientCeremonyId,
      },
    );
    return { ceremonyId: res.session_id, options: res.options };
  },
  complete: async (
    ceremonyId: string,
    assertion: AssertionResultWire,
    opts,
  ): Promise<void> => {
    await apiFetch<void>("/api/admin/v1/sessions/step-up", {
      method: "POST",
      body: { ceremony_id: ceremonyId, credential: assertion },
      tenantSlug: PLATFORM_TENANT_SLUG,
      ceremonyId: opts?.clientCeremonyId,
    });
  },
};

export function StepUpDialog() {
  const pending = useStepUpStore((s) => s.pending);
  const markVerified = useStepUpStore((s) => s.markVerified);
  const cancel = useStepUpStore((s) => s.cancel);
  const failPending = useStepUpStore((s) => s.failPending);

  // DD-coverage on the step-up surface — mirrors the portal
  // wrapper. The recommendation is normally already warm from
  // the admin sign-in page (15-min staleTime); calling here is
  // a cache hit in the common case. When undefined the dialog
  // falls back to its legacy bare surface.
  //
  // F-fix — opt into the authenticated mount so the handler
  // sees a non-nil xhttp.UserID and the recommender's layer-5
  // frontline routing fires. The admin sign-in page's cache
  // entry uses the public key; the step-up dialog uses the
  // authed key. Two cache entries, distinct keys, no
  // cross-contamination.
  const recommendation = useRecommendedDefault({ authed: true });

  const store: StepUpStoreBinding = {
    pending: pending !== null,
    reason: pending?.reason,
    markVerified,
    cancel,
    failPending,
  };

  return (
    <StepUpSurface
      client={adminStepUpClient}
      store={store}
      // Wires the step-up dialog's diagnostics layer:
      // ceremony id minted on every dialog OPEN, capability
      // probe + UA hint, classified failures rendered via
      // <CeremonyError>, fire-and-forget telemetry posted to
      // /api/client-telemetry. API_BASE is the API origin
      // from VITE_PUREID_API_URL.
      apiBaseUrl={API_BASE}
      recommendedDefault={recommendation.data}
    />
  );
}
